import { API_PATH, buildApiBaseUrl } from '@/services/ApiService';
import { deploymentAddressLabel, normalizeDeploymentAddress } from './deploymentAddress';

/**
 * Ask an address whether a FEATit deployment is there, and say plainly what came back.
 *
 * The point of this file is that "nothing answered" and "something answered and refused you" are
 * different sentences. An app that collapses them reports a dead deployment as a bad password and
 * a bad password as a dead deployment, and the reader tries the wrong repair — which is the defect
 * class of I-0173 restated as a naming problem rather than a spinner.
 *
 * The probe is unauthenticated on purpose. It has to answer before anybody has a credential, and
 * `/health` is the endpoint that both requires none and identifies the thing answering.
 *
 * One sentence it could not say used to be "it is there and will not serve you". A browser reports a
 * cross-origin refusal, a dead host and a rejected certificate as one indistinguishable error, so no
 * amount of care in here could separate them — the deployment had to cooperate. It does now:
 * `/deployment/capabilities` answers any origin and says whether this one is served (I-0197), which
 * is asked only when `/health` has already failed, so the ordinary path costs nothing.
 */

export const DEPLOYMENT_PROBE_OUTCOMES = {
  /** A FEATit deployment answered and is serving. */
  reachable: 'reachable',
  /** Nothing answered: no route, a refused connection, or no reply before the deadline. */
  unreachable: 'unreachable',
  /** A FEATit deployment answered and said it is not ready — starting, or its database is down. */
  unhealthy: 'unhealthy',
  /**
   * A FEATit deployment is there, and it will not serve an app running at this origin.
   *
   * Indistinguishable from `unreachable` in the browser — that is the whole reason this outcome
   * needs the deployment's cooperation to exist at all. See `askWhetherOriginIsServed`.
   */
  originRefused: 'origin-refused',
  /** Something is at that address; it is not a FEATit deployment. */
  notADeployment: 'not-a-deployment',
  /** What was typed is not a web address, so nothing was contacted. */
  invalidAddress: 'invalid-address',
} as const;

export type DeploymentProbeOutcomeType =
  (typeof DEPLOYMENT_PROBE_OUTCOMES)[keyof typeof DEPLOYMENT_PROBE_OUTCOMES];

export interface IDeploymentProbeResult {
  outcome: DeploymentProbeOutcomeType;
  /** The normalized address that was contacted, or null when nothing was. */
  address: string | null;
  /** One sentence saying what happened. */
  detail: string;
  /**
   * The things worth checking, as separate items rather than a paragraph.
   *
   * A run-on sentence listing four possible causes is read as one wall and acted on as none. Kept
   * out of `detail` so the screen can lay them out as a list, which is how somebody actually works
   * through them.
   */
  checks?: string[];
}

export interface IProbeDeploymentOptions {
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/**
 * Long enough for a cold container behind a proxy, short enough that nobody wonders.
 *
 * The 20 s request deadline the rest of the app uses is a budget for work; this is a budget for
 * somebody sitting in front of a first-run screen, and the two are not the same number.
 */
export const DEPLOYMENT_PROBE_TIMEOUT_MS = 8_000;

/** The API root for an address the reader named, by the same rule the build-time address uses. */
export function deploymentApiBaseUrl(address: string): string {
  return buildApiBaseUrl(address, API_PATH);
}

function isCrossOrigin(address: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(address).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Why "nothing answered" cannot be narrowed further from in here.
 *
 * A browser reports a refused connection, an unresolvable host, a rejected certificate and a
 * cross-origin refusal as the same opaque failure — deliberately, so a page cannot use fetch to map
 * a network it was not invited to. So the sentence names the possibilities instead of picking one,
 * because picking one would be a guess presented as a diagnosis.
 */
function unreachableResult(
  address: string,
  timedOut: boolean,
  timeoutMs: number,
): IDeploymentProbeResult {
  /*
   * This message names the address that was *dialled*, scheme and port included, where every other
   * message names the friendly label.
   *
   * The label strips the scheme, and the scheme is exactly what a failing address is most likely to
   * be wrong about: `100.78.180.2` was completed to `https://100.78.180.2`, dialled port 443, and
   * reported back as "Nothing answered at 100.78.180.2". The reader checks that host, finds it
   * serving happily on 6200, and concludes the app is broken — because the app named an address it
   * had not contacted. An unreachable report is the one place the exact target has to be visible.
   */
  const label = normalizeDeploymentAddress(address) ?? deploymentAddressLabel(address);
  if (timedOut) {
    return {
      outcome: DEPLOYMENT_PROBE_OUTCOMES.unreachable,
      address,
      detail: `${label} accepted the connection but sent no answer within ${Math.round(
        timeoutMs / 1000,
      )} seconds.`,
      checks: ['It may still be starting — try again in a moment.'],
    };
  }
  return {
    outcome: DEPLOYMENT_PROBE_OUTCOMES.unreachable,
    address,
    detail: `Nothing answered at ${label}.`,
    checks: [
      'Check the address is spelled the way you open it.',
      'Check the deployment is running.',
      'Check this device can reach it — a VPN or a private network may be in the way.',
      ...(isCrossOrigin(address)
        ? ['It may be refusing connections from an app served somewhere else.']
        : []),
    ],
  };
}

/**
 * Ask the one endpoint a refused browser can still read whether it would serve this app.
 *
 * `/deployment/capabilities` answers any origin (the backend's `accepted-origins.ts`), which makes
 * it the only way to tell "nothing is there" from "it is there and will not serve you". Those need
 * opposite repairs — check the address, versus ask its operator for one line of configuration — and
 * a browser reports both as the same empty failure.
 *
 * Returns null when the question cannot be answered: nothing replied, something replied that is not
 * a FEATit deployment, or the deployment predates the verdict. Null means "say the honest vague
 * thing", never "assume the worse of the two".
 */
async function askWhetherOriginIsServed(
  address: string,
  request: typeof fetch,
  signal: AbortSignal,
): Promise<boolean | null> {
  try {
    const response = await request(`${deploymentApiBaseUrl(address)}/deployment/capabilities`, {
      cache: 'no-store',
      signal,
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { product?: unknown; origin?: { accepted?: unknown } };
    if (body?.product !== 'featit') return null;
    // A deployment older than this field says nothing, and silence is not a refusal.
    return typeof body.origin?.accepted === 'boolean' ? body.origin.accepted : null;
  } catch {
    return null;
  }
}

function originRefusedResult(address: string): IDeploymentProbeResult {
  const label = deploymentAddressLabel(address);
  return {
    outcome: DEPLOYMENT_PROBE_OUTCOMES.originRefused,
    address,
    detail: `${label} is a FEATit deployment, but it does not serve apps running at ${window.location.origin}.`,
    checks: [
      `Its operator has to add ${window.location.origin} to MAIN_ADDITIONAL_ORIGINS and restart it.`,
      'The desktop app needs no such change — it is served from its own origin and every deployment accepts it.',
      'Opening that deployment directly in a browser works too.',
    ],
  };
}

function looksLikeDeploymentHealth(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const candidate = body as { status?: unknown; database?: unknown; writable?: unknown };
  if (typeof candidate.status !== 'string') return false;
  return typeof candidate.database === 'string' || typeof candidate.writable === 'boolean';
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function probeDeployment(
  rawAddress: string,
  { fetchImpl, timeoutMs = DEPLOYMENT_PROBE_TIMEOUT_MS }: IProbeDeploymentOptions = {},
): Promise<IDeploymentProbeResult> {
  const address = normalizeDeploymentAddress(rawAddress);
  if (address === null) {
    return {
      outcome: DEPLOYMENT_PROBE_OUTCOMES.invalidAddress,
      address: null,
      detail:
        'That is not a web address. A deployment address looks like https://featit.example.com.',
    };
  }

  const label = deploymentAddressLabel(address);
  const request = fetchImpl ?? globalThis.fetch;
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await request(`${deploymentApiBaseUrl(address)}/health`, {
      cache: 'no-store',
      signal: controller.signal,
    });

    if (response.ok) {
      const body = await readJson(response);
      if (looksLikeDeploymentHealth(body)) {
        return {
          outcome: DEPLOYMENT_PROBE_OUTCOMES.reachable,
          address,
          detail: `${label} answered.`,
        };
      }
      return {
        outcome: DEPLOYMENT_PROBE_OUTCOMES.notADeployment,
        address,
        detail: `${label} answered, but it is not a FEATit deployment.`,
      };
    }

    if (response.status === 503 || response.status === 502 || response.status === 504) {
      const body = await readJson(response);
      const detail = looksLikeDeploymentHealth(body)
        ? `${label} is a FEATit deployment, but it is not ready to serve yet.`
        : `${label} answered, but nothing is serving there yet. It may still be starting.`;
      return { outcome: DEPLOYMENT_PROBE_OUTCOMES.unhealthy, address, detail };
    }

    return {
      outcome: DEPLOYMENT_PROBE_OUTCOMES.notADeployment,
      address,
      detail: `${label} answered with ${response.status}, which is not a FEATit deployment.`,
    };
  } catch {
    /*
     * Only on the failure path, and only when the origins differ, so the ordinary probe still costs
     * one request and no extra wait. Same deadline: the controller already carries whatever is left
     * of it, and a second budget would let a first-run screen hang for twice as long.
     */
    if (!timedOut && isCrossOrigin(address)) {
      const served = await askWhetherOriginIsServed(address, request, controller.signal);
      if (served === false) return originRefusedResult(address);
      if (served === true) {
        // Capabilities answered, so the deployment is there and does serve this origin — which
        // leaves `/health` itself as the thing that failed. Saying "nothing answered" here would
        // send the reader to check an address that is demonstrably correct.
        return {
          outcome: DEPLOYMENT_PROBE_OUTCOMES.unhealthy,
          address,
          detail: `${deploymentAddressLabel(address)} is a FEATit deployment, but it is not ready to serve yet.`,
        };
      }
    }
    return unreachableResult(address, timedOut, timeoutMs);
  } finally {
    clearTimeout(timer);
  }
}
